/**
 * The editor's left rail on bulk send's upload path: the roles a document is
 * laid out for, named here rather than picked from people on an earlier step.
 *
 * It hands the shared editor a `rolesRail` slot, so nothing about bulk leaks
 * into `SignRequestEditor` — the field palette below it, field placement and
 * the properties panel all behave exactly as they do for a normal request.
 *
 * Role ids stay small and 0-based on purpose: a field's colour is looked up as
 * `fieldColor[field.signerIndex]` (`DraggableField.tsx:76`), so an id outside
 * that palette's range would leave the page and this rail disagreeing.
 */

import React from "react";
import { Box, Tooltip, Typography } from "@mui/material";
import { RequestFile, FileField, RequestParticipant } from "models/interfaces";
import {
  deleteSigner,
  setReduxSelectedParticipant,
  setReduxSignRequest,
  updateIndividualSigner,
} from "redux/appSlice";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { RootState } from "utils/store";
import { fieldColor } from "screens/SignRequest/SignRequestHelper";
import { openErrorNotification } from "helpers/methods";
import { rolesRailStyles } from "../BulkSend.styles";
import tokens from "../BulkSend.tokens";
import { orderLabel } from "../BulkSendHelper";
import { CloseIcon, PlusIcon } from "./BulkSendIcons";

const p = tokens.palette;
const t = tokens.rolesRail;
const s = rolesRailStyles;

/** Colours run out before the editor's own limit does. */
const MAX_ROLES = fieldColor.length;

const BulkRolesRail = () => {
  const dispatch = useAppDispatch();
  const { signRequest, selectedParticipant } = useAppSelector(
    (state: RootState) => state.app,
  );

  const roles: RequestParticipant[] = signRequest?.signers ?? [];

  /** How many fields are placed for a role — the rail's own progress signal. */
  const fieldCountFor = (signerIndex: number): number =>
    (signRequest?.files ?? []).reduce(
      (total: number, file: RequestFile) =>
        total +
        (file.fields ?? []).filter(
          (field: FileField) => field.signerIndex === signerIndex,
        ).length,
      0,
    );

  const selectRole = (role: RequestParticipant) =>
    dispatch(setReduxSelectedParticipant(role));

  const renameRole = (signerId: number, value: string) =>
    dispatch(
      updateIndividualSigner({
        signerId,
        fieldsToUpdate: { requestRole: value },
      }),
    );

  const addRole = () => {
    if (roles.length >= MAX_ROLES) {
      openErrorNotification(`A document can have up to ${MAX_ROLES} roles.`);
      return;
    }
    const nextId =
      roles.reduce((highest, role) => Math.max(highest, Number(role.id)), -1) +
      1;
    const nextRole: RequestParticipant = {
      id: nextId,
      name: "",
      email: "",
      requestRole: `Signer ${roles.length + 1}`,
      order: roles.length + 1,
      color: fieldColor[nextId],
      cc: false,
    };
    dispatch(setReduxSignRequest({ signers: [...roles, nextRole] }));
    dispatch(setReduxSelectedParticipant(nextRole));
  };

  const removeRole = (role: RequestParticipant) => {
    if (roles.length === 1) {
      openErrorNotification("A document needs at least one role.");
      return;
    }
    dispatch(deleteSigner({ signerId: role.id }));
    if (selectedParticipant?.id === role.id) {
      const fallback = roles.find((candidate) => candidate.id !== role.id);
      if (fallback) dispatch(setReduxSelectedParticipant(fallback));
    }
  };

  /** Signing order is the rail's own list order, so moving swaps both. */
  const moveRole = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= roles.length) return;
    const reordered = [...roles];
    const moved = reordered[index];
    reordered[index] = reordered[target];
    reordered[target] = moved;
    dispatch(
      setReduxSignRequest({
        signers: reordered.map((role, position) => ({
          ...role,
          order: position + 1,
        })),
      }),
    );
  };

  return (
    <Box sx={s.root}>
      <Box sx={s.header}>
        <Typography component="span" sx={s.eyebrow}>
          Placing fields for
        </Typography>
        <Typography component="span" sx={s.hint}>
          Name each role and place its fields. You'll say who fills them in the
          next step.
        </Typography>
      </Box>

      <Box sx={s.list}>
        {roles.map((role, index) => {
          const isActive = selectedParticipant?.id === role.id;
          const count = fieldCountFor(role.id);
          return (
            <Box
              key={role.id}
              onClick={() => selectRole(role)}
              sx={s.card(isActive)}
            >
              <Box sx={s.cardRow}>
                <Box sx={s.badge(fieldColor[role.id] || p.accent)}>
                  {index + 1}
                </Box>
                <Box
                  component="input"
                  value={role.requestRole ?? ""}
                  placeholder="Role name"
                  aria-label={`Name for role ${index + 1}`}
                  onFocus={() => selectRole(role)}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    renameRole(role.id, event.target.value)
                  }
                  sx={s.input(isActive)}
                />
                <Tooltip title="Remove role">
                  <Box
                    component="button"
                    type="button"
                    aria-label={`Remove role ${index + 1}`}
                    onClick={(event: React.MouseEvent) => {
                      event.stopPropagation();
                      removeRole(role);
                    }}
                    sx={s.remove}
                  >
                    <CloseIcon size={t.removeIconSize} />
                  </Box>
                </Tooltip>
              </Box>

              <Box sx={s.meta}>
                <Box component="span" sx={s.orderPill}>
                  {orderLabel(role.order)}
                </Box>
                <Box component="span" sx={s.fieldCount(count === 0)}>
                  {count} {count === 1 ? "field" : "fields"}
                </Box>
                <Box sx={s.arrows}>
                  <Box
                    component="button"
                    type="button"
                    aria-label={`Move role ${index + 1} earlier`}
                    disabled={index === 0}
                    onClick={(event: React.MouseEvent) => {
                      event.stopPropagation();
                      moveRole(index, -1);
                    }}
                    sx={s.arrow(index === 0)}
                  >
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2.5}
                      strokeLinecap="round"
                      style={{ width: t.arrowWidth, height: t.arrowHeight }}
                      aria-hidden
                    >
                      <path d="M6 15l6-6 6 6" />
                    </svg>
                  </Box>
                  <Box
                    component="button"
                    type="button"
                    aria-label={`Move role ${index + 1} later`}
                    disabled={index === roles.length - 1}
                    onClick={(event: React.MouseEvent) => {
                      event.stopPropagation();
                      moveRole(index, 1);
                    }}
                    sx={s.arrow(index === roles.length - 1)}
                  >
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2.5}
                      strokeLinecap="round"
                      style={{ width: t.arrowWidth, height: t.arrowHeight }}
                      aria-hidden
                    >
                      <path d="M6 9l6 6 6-6" />
                    </svg>
                  </Box>
                </Box>
              </Box>
            </Box>
          );
        })}

        <Box component="button" type="button" onClick={addRole} sx={s.add}>
          <PlusIcon size={t.addIconSize} />
          Add a role
        </Box>
      </Box>
    </Box>
  );
};

export default BulkRolesRail;
